// ============================================================================
// CANCUN100 — useBorrador.js  (Bloque 27: los formularios no se vacían cuando algo falla)
// ----------------------------------------------------------------------------
// Borrador automático en el navegador (localStorage), clave `c100:borrador:<formulario>:<quien>`.
//   · Cada cambio se guarda con debounce de 300 ms. Nunca se guardan PIN, contraseñas ni archivos
//     (llaves que contengan pin/password/contrasena y valores File/Blob se descartan).
//   · Al montar (o al activarse) con borrador vigente: se prellena y `recuperado` trae la fecha,
//     para la banda "Recuperamos lo que estabas capturando · [Seguir] [Empezar en blanco]".
//   · Al guardar con éxito el formulario llama `limpiar()`. Los borradores de más de 24 h se descartan.
//   · Si el estado es "vacío" (igual al inicial o según `esVacio`) no se guarda y se borra el que hubiera.
// Uso: const borrador = useBorrador(claveBorrador('reserva', quien), estado, setEstado, { activo, inicial, esVacio });
// ============================================================================
import { useEffect, useRef, useState, useCallback } from 'react';

const PREFIJO = 'c100:borrador:';
const CADUCIDAD_MS = 24 * 60 * 60 * 1000;
const DEBOUNCE_MS = 300;
const LLAVE_PROHIBIDA = /pin|password|contrasen|contraseñ|clabe/i; // Bloque 28: la CLABE tampoco va al borrador

export const claveBorrador = (formulario, quien) => `${PREFIJO}${formulario}:${quien == null || quien === '' ? 'anon' : quien}`;

const esArchivo = (v) => (typeof File !== 'undefined' && v instanceof File) || (typeof Blob !== 'undefined' && v instanceof Blob);

// Deja solo texto/números/booleanos/selecciones serializables. Sin credenciales ni archivos.
export const limpiarDatos = (v) => {
  if (v == null) return v;
  if (esArchivo(v)) return undefined;
  if (Array.isArray(v)) return v.map(limpiarDatos).filter((x) => x !== undefined);
  if (typeof v === 'object') {
    if (v instanceof Date) return v.toISOString();
    const out = {};
    Object.keys(v).forEach((k) => {
      if (LLAVE_PROHIBIDA.test(k)) return;
      const x = limpiarDatos(v[k]);
      if (x !== undefined) out[k] = x;
    });
    return out;
  }
  if (typeof v === 'function' || typeof v === 'symbol') return undefined;
  return v;
};

export const leerBorrador = (clave) => {
  try {
    const raw = localStorage.getItem(clave);
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (!b || typeof b !== 'object' || !b.en || Date.now() - b.en > CADUCIDAD_MS) { localStorage.removeItem(clave); return null; }
    return b;
  } catch { return null; }
};
export const guardarBorrador = (clave, datos) => {
  try { localStorage.setItem(clave, JSON.stringify({ en: Date.now(), datos: limpiarDatos(datos) })); } catch { /* sin storage */ }
};
export const borrarBorrador = (clave) => { try { localStorage.removeItem(clave); } catch { /* sin storage */ } };
export const hayBorrador = (clave) => !!leerBorrador(clave);

const igual = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } };

export const fmtFechaBorrador = (ms) => {
  try { return new Date(ms).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

export default function useBorrador(clave, estado, setEstado, { activo = true, inicial = null, esVacio = null } = {}) {
  const [recuperado, setRecuperado] = useState(null); // { en } mientras se muestra la banda
  const timer = useRef(null);
  const listo = useRef(false);        // ya pasó la restauración de esta clave
  const ultimo = useRef(null);        // último JSON guardado (evita escrituras repetidas)
  const refEstado = useRef(estado); refEstado.current = estado;
  const refSet = useRef(setEstado); refSet.current = setEstado;
  const vacio = useCallback((e) => (esVacio ? !!esVacio(e) : (inicial != null ? igual(limpiarDatos(e), limpiarDatos(inicial)) : false)), [esVacio, inicial]);

  // Restaurar al montar / al cambiar de clave / al activarse.
  useEffect(() => {
    listo.current = false; ultimo.current = null; setRecuperado(null);
    if (!activo) return;
    const b = leerBorrador(clave);
    if (b && b.datos && !vacio(b.datos)) {
      try { refSet.current(b.datos); } catch { /* estado incompatible */ }
      setRecuperado({ en: b.en });
      ultimo.current = JSON.stringify(limpiarDatos(b.datos));
    }
    listo.current = true;
  }, [clave, activo, vacio]);

  // Guardar con debounce.
  useEffect(() => {
    if (!activo || !listo.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const e = refEstado.current;
      if (vacio(e)) { if (ultimo.current !== null) { borrarBorrador(clave); ultimo.current = null; } return; }
      let json; try { json = JSON.stringify(limpiarDatos(e)); } catch { return; }
      if (json === ultimo.current) return;
      ultimo.current = json;
      guardarBorrador(clave, e);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [estado, clave, activo, vacio]);

  const limpiar = useCallback(() => { clearTimeout(timer.current); borrarBorrador(clave); ultimo.current = null; setRecuperado(null); }, [clave]);
  const seguir = useCallback(() => setRecuperado(null), []);
  const empezarEnBlanco = useCallback(() => {
    limpiar();
    if (inicial != null) { try { refSet.current(inicial); } catch { /* sin inicial */ } }
  }, [limpiar, inicial]);

  return { recuperado, seguir, empezarEnBlanco, limpiar };
}

// Banda discreta "Recuperamos lo que estabas capturando". Se pinta donde el formulario quiera.
export function BandaBorrador({ C, borrador, compacta = false }) {
  if (!borrador || !borrador.recuperado) return null;
  const hora = fmtFechaBorrador(borrador.recuperado.en);
  const boton = { height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap' };
  return (
    <div role="status" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: compacta ? 10 : 14, padding: compacta ? '8px 12px' : '10px 14px', background: `${C.blue}12`, border: `1px solid ${C.blue}45`, borderRadius: 'var(--au-radius-lg)', fontSize: 14, color: C.text }}>
      <div><strong>Recuperamos lo que estabas capturando</strong>{hora && <span style={{ color: C.muted }}> · {hora}</span>}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="au-btn" style={{ ...boton, background: 'var(--equipo-grad)', color: '#fff', border: '1px solid transparent' }} onClick={borrador.seguir}>Seguir</button>
        <button type="button" className="au-btn au-btn--ghost" style={boton} onClick={borrador.empezarEnBlanco}>Empezar en blanco</button>
      </div>
    </div>
  );
}
